import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  NumberInput,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Text,
  Badge,
  Alert,
  Divider,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle } from "@tabler/icons-react";
import { StatCard } from "../../components/common/StatCard";
import { DataTable, type Column } from "../../components/common/DataTable";
import { settlementsApi } from "../../api/settlements";
import { usePermission } from "../../auth/usePermission";
import { errorMessage } from "../../lib/axios";
import { formatMoney, formatDateTime } from "../../lib/format";
import { humanize } from "../../lib/labels";
import type { CommissionRecord } from "../../types/domain";

/**
 * Per-driver finance view: running balance, cash-limit override, and the
 * commission ledger (one row per completed trip).
 */
export function DriverFinanceTab({
  driverId,
  currentOverride,
}: {
  driverId: string;
  currentOverride?: number | null;
}) {
  const qc = useQueryClient();
  const [override, setOverride] = useState<number | "">("");

  useEffect(() => {
    setOverride(
      currentOverride === null || currentOverride === undefined
        ? ""
        : currentOverride,
    );
  }, [currentOverride]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["driver-commission", driverId],
    queryFn: () => settlementsApi.driverCommission(driverId, { limit: 25 }),
  });

  const canManage = usePermission("finance.manage");

  // Takes the value explicitly: "Clear override" calls setOverride("") and
  // mutates in the same tick, so reading `override` here would save the old one.
  const limitMutation = useMutation({
    mutationFn: (limit: number | null) =>
      settlementsApi.setDebtLimit(driverId, limit),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Cash limit updated" });
      qc.invalidateQueries({ queryKey: ["driver", driverId] });
      qc.invalidateQueries({ queryKey: ["driver-commission", driverId] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const balance = data?.balance;

  const columns: Column<CommissionRecord>[] = [
    {
      key: "completedAt",
      header: "Completed",
      render: (r) => formatDateTime(r.completedAt),
    },
    {
      key: "paymentMode",
      header: "Paid by",
      render: (r) => humanize(r.paymentMode ?? ""),
    },
    {
      key: "platformCommission",
      header: "Commission",
      align: "right",
      render: (r) => formatMoney(r.platformCommission),
    },
    {
      key: "bookingFee",
      header: "Booking fee",
      align: "right",
      render: (r) => formatMoney(r.bookingFee),
    },
    {
      key: "latraLevy",
      header: "LATRA levy",
      align: "right",
      render: (r) => formatMoney(r.latraLevy ?? 0),
    },
    {
      key: "debtDelta",
      header: "Balance effect",
      align: "right",
      render: (r) => (
        <Text size="sm" c={(r.debtDelta ?? 0) > 0 ? "var(--error-fg)" : "var(--success-fg)"}>
          {(r.debtDelta ?? 0) > 0 ? "+" : ""}
          {formatMoney(r.debtDelta)}
        </Text>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge
          color={r.status === "collected" ? "green" : "yellow"}
          variant="light"
        >
          {humanize(r.status ?? "")}
        </Badge>
      ),
    },
    {
      key: "collectionMethod",
      header: "Collected via",
      render: (r) =>
        r.collectionMethod ? (
          humanize(r.collectionMethod)
        ) : (
          <Text c="dimmed">—</Text>
        ),
    },
  ];

  return (
    <Stack>
      {balance && (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
          <StatCard
            label="Running balance"
            value={formatMoney(balance.platformDebt)}
            sub={
              balance.platformDebt > 0
                ? "Driver owes Smart Safiri"
                : balance.platformDebt < 0
                  ? "Smart Safiri owes driver"
                  : "Settled"
            }
          />
          <StatCard
            label="Pending commission"
            value={formatMoney(balance.pendingCommission)}
            sub="Not yet collected"
          />
          <StatCard
            label="Effective limit"
            value={formatMoney(balance.maxDebt)}
            sub={
              currentOverride === null || currentOverride === undefined
                ? "Global default"
                : "Per-driver override"
            }
          />
          <StatCard
            label="Carried credit"
            value={formatMoney(balance.unallocatedCredit)}
            sub="Unallocated settlement"
          />
        </SimpleGrid>
      )}

      {balance?.isLockedDueToDebt && (
        <Alert
          variant="light"
          color="red"
          icon={<IconAlertTriangle size={16} />}
        >
          Locked for debt: balance of {formatMoney(balance.platformDebt)}{" "}
          exceeds the {formatMoney(balance.maxDebt)} limit. The driver will not
          be offered new trips until it clears. A trip already in progress when
          the limit was crossed was allowed to finish.
        </Alert>
      )}

      {canManage && (
      <Card withBorder radius="md">
        <Stack>
          <Text size="sm" fw={600}>
            Cash limit override
          </Text>
          <Text size="xs" c="dimmed">
            Leave blank to use the global default from Settings. 0 means this
            driver may carry no cash float at all.
          </Text>
          <Group align="flex-end">
            <NumberInput
              label="Max debt (TZS)"
              placeholder="Using global default"
              value={override}
              onChange={(val) =>
                setOverride(val === "" || val === null ? "" : Number(val))
              }
              min={0}
              thousandSeparator=","
              w={240}
            />
            <Button
              loading={limitMutation.isPending}
              onClick={() =>
                limitMutation.mutate(override === "" ? null : Number(override))
              }
            >
              Save limit
            </Button>
            {override !== "" && (
              <Button
                variant="subtle"
                onClick={() => {
                  setOverride("");
                  limitMutation.mutate(null);
                }}
              >
                Clear override
              </Button>
            )}
          </Group>
        </Stack>
      </Card>
      )}

      <Card withBorder radius="md" p="xs">
        <Divider label="Commission ledger" labelPosition="left" mb="xs" />
        <DataTable<CommissionRecord>
          columns={columns}
          rows={data?.records ?? []}
          rowKey={(r) => r._id}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel="No commission records yet"
        />
      </Card>
    </Stack>
  );
}
